import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, FolderTree, Pencil, X, Save } from 'lucide-react';
import AdminLayout from '../../components/Layout/AdminLayout';
import Toast, { useToasts } from '../../components/Layout/Toast';
import { monitorApi } from '../../services/api';
import { MonitorGroup, Recipient } from '../../types';

type HeaderEntry = { id: string; key: string; value: string };

const parseHeaderEntries = (raw: string | null | undefined): HeaderEntry[] => {
  const input = (raw || '').trim();
  if (!input) return [{ id: 'h1', key: '', value: '' }];

  if (input.startsWith('{')) {
    try {
      const obj = JSON.parse(input) as Record<string, unknown>;
      const entries = Object.entries(obj).map(([k, v], idx) => ({ id: `h${idx + 1}`, key: k, value: String(v ?? '') }));
      return entries.length > 0 ? entries : [{ id: 'h1', key: '', value: '' }];
    } catch {
      // Fall through to line parsing
    }
  }

  const lineEntries = input
    .split('\n')
    .map((line, idx) => {
      const i = line.indexOf(':');
      if (i <= 0) return null;
      return {
        id: `h${idx + 1}`,
        key: line.slice(0, i).trim(),
        value: line.slice(i + 1).trim(),
      };
    })
    .filter(Boolean) as HeaderEntry[];

  return lineEntries.length > 0 ? lineEntries : [{ id: 'h1', key: '', value: '' }];
};

const toHeaderString = (entries: HeaderEntry[]): string =>
  entries
    .map(e => ({ key: e.key.trim(), value: e.value.trim() }))
    .filter(e => e.key && e.value)
    .map(e => `${e.key}: ${e.value}`)
    .join('\n');

const AdminGroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<MonitorGroup[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [headerEntries, setHeaderEntries] = useState<HeaderEntry[]>([{ id: 'h1', key: '', value: '' }]);
  const [queryParams, setQueryParams] = useState('');
  const [defaultRecipients, setDefaultRecipients] = useState<number[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editHeaderEntries, setEditHeaderEntries] = useState<HeaderEntry[]>([{ id: 'h1', key: '', value: '' }]);
  const [editQueryParams, setEditQueryParams] = useState('');
  const [editDefaultRecipients, setEditDefaultRecipients] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToasts();

  const load = useCallback(async () => {
    try {
      const [groupRes, recipientRes] = await Promise.all([
        monitorApi.getAdminGroups(),
        monitorApi.getRecipients(),
      ]);
      setGroups(groupRes.data);
      setRecipients(recipientRes.data);
    }
    catch { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await monitorApi.createGroup({ name, description, headers: toHeaderString(headerEntries), query_params: queryParams });
      if (defaultRecipients.length > 0) {
        await monitorApi.setGroupDefaultRecipients(res.data.id, defaultRecipients);
      }
      setName('');
      setDescription('');
      setHeaderEntries([{ id: 'h1', key: '', value: '' }]);
      setQueryParams('');
      setDefaultRecipients([]);
      toast.success('Group created');
      await load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to create group'); }
  };

  const remove = async (g: MonitorGroup) => {
    if (!window.confirm(`Delete group "${g.name}"? Monitors will be ungrouped.`)) return;
    try { await monitorApi.deleteGroup(g.id); toast.success('Group deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const startEdit = (g: MonitorGroup) => {
    setEditingId(g.id);
    setEditName(g.name);
    setEditDescription(g.description || '');
    setEditHeaderEntries(parseHeaderEntries(g.headers || ''));
    setEditQueryParams(g.query_params || (g as any).queryParams || '');
    setEditDefaultRecipients(g.default_recipient_ids || []);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditHeaderEntries([{ id: 'h1', key: '', value: '' }]);
    setEditQueryParams('');
    setEditDefaultRecipients([]);
  };

  const saveEdit = async (g: MonitorGroup) => {
    if (!editName.trim()) {
      toast.error('Group name is required');
      return;
    }
    try {
      await monitorApi.updateGroup(g.id, { name: editName.trim(), description: editDescription.trim(), headers: toHeaderString(editHeaderEntries), query_params: editQueryParams });
      await monitorApi.setGroupDefaultRecipients(g.id, editDefaultRecipients);
      toast.success('Group updated');
      cancelEdit();
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  };

  const addHeaderEntry = (mode: 'create' | 'edit') => {
    const next = { id: `h${Date.now()}`, key: '', value: '' };
    if (mode === 'create') setHeaderEntries(prev => [...prev, next]);
    else setEditHeaderEntries(prev => [...prev, next]);
  };

  const removeHeaderEntry = (mode: 'create' | 'edit', id: string) => {
    if (mode === 'create') {
      setHeaderEntries(prev => {
        const next = prev.filter(h => h.id !== id);
        return next.length > 0 ? next : [{ id: 'h1', key: '', value: '' }];
      });
      return;
    }
    setEditHeaderEntries(prev => {
      const next = prev.filter(h => h.id !== id);
      return next.length > 0 ? next : [{ id: 'h1', key: '', value: '' }];
    });
  };

  const updateHeaderEntry = (mode: 'create' | 'edit', id: string, field: 'key' | 'value', value: string) => {
    if (mode === 'create') {
      setHeaderEntries(prev => prev.map(h => (h.id === id ? { ...h, [field]: value } : h)));
      return;
    }
    setEditHeaderEntries(prev => prev.map(h => (h.id === id ? { ...h, [field]: value } : h)));
  };

  const input = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40';
  const selectableRecipients = recipients.filter(r => r.scope === 'global' || (r.scope === 'group' && r.group_id !== null));

  const toggleCreateDefaultRecipient = (id: number) => {
    setDefaultRecipients(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const toggleEditDefaultRecipient = (id: number) => {
    setEditDefaultRecipients(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  return (
    <AdminLayout>
      <Toast toasts={toast.toasts} onDismiss={toast.dismiss} />
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-1">Groups</h1>
        <p className="text-sm text-slate-500 mb-6">Organize monitors and route group-level alert recipients.</p>

        <form onSubmit={add} className="bg-white rounded-xl border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Group</label>
            <input className={input} value={name} onChange={e => setName(e.target.value)} required placeholder="Team Name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Description</label>
            <input className={input} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-600 mb-1">Default Request Headers</label>
            <div className="border border-slate-300 rounded-md p-3 space-y-2">
              {headerEntries.map((entry, idx) => (
                <div key={entry.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <input
                    className={`${input} font-mono text-xs`}
                    placeholder="Header name"
                    value={entry.key}
                    onChange={e => updateHeaderEntry('create', entry.id, 'key', e.target.value)}
                  />
                  <input
                    className={`${input} font-mono text-xs`}
                    placeholder="Header value"
                    value={entry.value}
                    onChange={e => updateHeaderEntry('create', entry.id, 'value', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeHeaderEntry('create', entry.id)}
                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded"
                    title={`Remove header ${idx + 1}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addHeaderEntry('create')}
                className="inline-flex items-center gap-2 text-xs font-medium text-brand hover:opacity-80"
              >
                <Plus size={14} /> Add header
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Applied to monitors in this group. Monitor-level headers override duplicate keys.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-600 mb-1">Default Query Params</label>
            <input
              className={input}
              value={queryParams}
              onChange={e => setQueryParams(e.target.value)}
              placeholder="iam=uptime"
            />
            <p className="text-xs text-slate-400 mt-1">Applied to monitors in this group during checks. Existing URL query keys on monitor URLs are preserved.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-600 mb-1">Default Recipients For New Monitors In This Group</label>
            <div className="border border-slate-300 rounded-md p-3">
              <div className="max-h-36 overflow-auto space-y-1">
                {selectableRecipients.length === 0 ? (
                  <div className="text-xs text-slate-400">No recipients available.</div>
                ) : selectableRecipients.map(r => (
                  <label key={r.id} className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={defaultRecipients.includes(r.id)}
                      onChange={() => toggleCreateDefaultRecipient(r.id)}
                    />
                    <span>{r.email}</span>
                    <span className="text-slate-400">({r.scope}{r.group_name ? `: ${r.group_name}` : ''})</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div>
          <button type="submit" className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-brand text-white rounded-md hover:bg-brand-dark h-[38px]">
            <Plus size={16} /> Add
          </button>
          </div>
        </form>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-5 py-16 text-center text-slate-400">Loading…</div>
          ) : groups.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No groups yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {groups.map(g => (
                <div key={g.id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><FolderTree size={18} /></div>
                    {editingId === g.id ? (
                      <div className="space-y-2 min-w-[360px]">
                        <input
                          className={`${input} min-w-[240px]`}
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          placeholder="Group name"
                        />
                        <input
                          className={`${input} min-w-[240px]`}
                          value={editDescription}
                          onChange={e => setEditDescription(e.target.value)}
                          placeholder="Description"
                        />
                        <input
                          className={`${input} min-w-[240px]`}
                          value={editQueryParams}
                          onChange={e => setEditQueryParams(e.target.value)}
                          placeholder="thisis=dcxuptime&source=groupdefault"
                        />
                        <div className="border border-slate-300 rounded-md p-3 space-y-2 min-w-[240px]">
                          {editHeaderEntries.map((entry, idx) => (
                            <div key={entry.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                              <input
                                className={`${input} font-mono text-xs`}
                                placeholder="Header name"
                                value={entry.key}
                                onChange={e => updateHeaderEntry('edit', entry.id, 'key', e.target.value)}
                              />
                              <input
                                className={`${input} font-mono text-xs`}
                                placeholder="Header value"
                                value={entry.value}
                                onChange={e => updateHeaderEntry('edit', entry.id, 'value', e.target.value)}
                              />
                              <button
                                type="button"
                                onClick={() => removeHeaderEntry('edit', entry.id)}
                                className="p-2 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded"
                                title={`Remove header ${idx + 1}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addHeaderEntry('edit')}
                            className="inline-flex items-center gap-2 text-xs font-medium text-brand hover:opacity-80"
                          >
                            <Plus size={14} /> Add header
                          </button>
                        </div>
                        <div className="border border-slate-300 rounded-md p-3 min-w-[240px]">
                          <div className="text-xs font-medium text-slate-600 mb-2">Default Recipients For New Monitors In This Group</div>
                          <div className="max-h-36 overflow-auto space-y-1">
                            {selectableRecipients.length === 0 ? (
                              <div className="text-xs text-slate-400">No recipients available.</div>
                            ) : selectableRecipients.map(r => (
                              <label key={r.id} className="flex items-center gap-2 text-xs text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={editDefaultRecipients.includes(r.id)}
                                  onChange={() => toggleEditDefaultRecipient(r.id)}
                                />
                                <span>{r.email}</span>
                                <span className="text-slate-400">({r.scope}{r.group_name ? `: ${r.group_name}` : ''})</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-medium text-slate-800">{g.name}</div>
                        <div className="text-xs text-slate-400">{g.description || 'No description'} · {g.monitor_count || 0} monitor(s)</div>
                        {(g.headers || '').trim() && <div className="text-[11px] text-slate-500 mt-1">Has default headers configured</div>}
                        {(g.query_params || '').trim() && <div className="text-[11px] text-slate-500 mt-1">Default query params: ?{g.query_params}</div>}
                        {(g.default_recipient_emails || []).length > 0 && (
                          <div className="text-[11px] text-slate-500 mt-1">Default recipients: {(g.default_recipient_emails || []).join(', ')}</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {editingId === g.id ? (
                      <>
                        <button onClick={() => saveEdit(g)} className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded" title="Save"><Save size={16} /></button>
                        <button onClick={cancelEdit} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded" title="Cancel"><X size={16} /></button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEdit(g)}
                        disabled={editingId !== null}
                        className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 disabled:cursor-not-allowed"
                        title={editingId !== null ? 'Finish current edit first' : 'Edit'}
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => remove(g)}
                      disabled={editingId !== null && editingId !== g.id}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 disabled:cursor-not-allowed"
                      title={editingId !== null && editingId !== g.id ? 'Finish current edit first' : 'Delete'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminGroupsPage;
